import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { XIcon } from 'lucide-react';
import { toast } from 'react-toastify';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import { useSitrepAutoRefresh } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { useAorReportApprovalPolicy } from 'components/Reports/hooks/useAorReportApprovalPolicy';
import type {
  AutoRefreshNotifyChannel,
  AutoRefreshNotifyRecipient,
  AutoRefreshScheduleType,
  GetSitrepsResponse,
} from 'components/Reports/reports.types';
import { Checkbox } from '@/components/ui/checkbox';
import { ReachabilityWarning } from 'components/MapPage/CorporateViewTabs/NotificationsTab/ReachabilityWarning';
import {
  roleScopeGroups,
  roleScopeKey,
  type RoleScopeMember,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/audienceModel';
import { RoleScopePicker } from 'components/Reports/RoleScopePicker';
import apiConfig from 'constants/apiConfig';
import {
  maxIntervalAmountForUnit,
  minutesToInterval,
  validIntervalMinutes,
  type AutoRefreshIntervalUnit,
} from 'components/Reports/utils/sitrepAutoRefreshInterval';
import {
  autoRefreshRunSummaryText,
  browserTimeZone,
  DEFAULT_TIME_OF_DAY,
  describeSchedule,
  parseScheduleType,
  sortedDays,
  validateSchedule,
  WEEKDAYS,
} from 'components/Reports/utils/sitrepAutoRefreshSchedule';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

type SitrepAutoRefreshControlsProps = {
  sitrep: GetSitrepsResponse;
  /**
   * When false, edits stay local until `save()` on the ref. Used by the draft
   * menu dialog with explicit Save / Cancel.
   */
  persistOnChange?: boolean;
};

export type SitrepAutoRefreshControlsHandle = {
  save: () => Promise<boolean>;
  isPending: boolean;
};

type NotifyRecipient = AutoRefreshNotifyRecipient;

type ScheduleTiming = {
  scheduleType: AutoRefreshScheduleType;
  timeOfDay: string;
  daysOfWeek: number[];
};

const timingKey = (timing: ScheduleTiming) =>
  timing.scheduleType === 'interval'
    ? 'interval'
    : `${timing.scheduleType}@${timing.timeOfDay}:${timing.scheduleType === 'weekly' ? sortedDays(timing.daysOfWeek).join(',') : ''}`;

const SCHEDULE_TYPE_LABELS: Record<AutoRefreshScheduleType, string> = {
  interval: 'Every…',
  daily: 'Daily',
  weekly: 'Weekly',
};

const recipientKey = (recipient: NotifyRecipient) =>
  recipient.kind === 'user'
    ? `user:${recipient.userGuid}`
    : `role:${recipient.roleId}|${recipient.scopeId ?? '*'}`;

const sameRecipients = (left: NotifyRecipient[], right: NotifyRecipient[]) => {
  const keys = (items: NotifyRecipient[]) =>
    items.map(recipientKey).sort().join('|');
  return keys(left) === keys(right);
};

const AUTO_REFRESH_CHANNEL_OPTIONS: Array<{
  value: AutoRefreshNotifyChannel;
  label: string;
}> = [
  { value: 'inApp', label: 'In-app' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const sameChannels = (
  left: AutoRefreshNotifyChannel[],
  right: AutoRefreshNotifyChannel[]
) => [...left].sort().join('|') === [...right].sort().join('|');

const formatLocalTime = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const time = new Date(value);
  if (!Number.isFinite(time.getTime())) return null;
  return time.toLocaleString();
};

const SitrepAutoRefreshControls = forwardRef<
  SitrepAutoRefreshControlsHandle,
  SitrepAutoRefreshControlsProps
>(({ sitrep, persistOnChange = true }, ref) => {
  const mutation = useSitrepAutoRefresh();
  const directory = useAorReportApprovalPolicy(sitrep.aor_id);
  const saved = sitrep.auto_refresh;
  const savedMinutes = saved?.intervalMinutes ?? 60;
  const savedRecipients = saved?.notifyRecipients ?? [];
  const savedChannels: AutoRefreshNotifyChannel[] = saved?.notifyChannels
    ?.length
    ? saved.notifyChannels
    : ['inApp'];
  const initial = minutesToInterval(savedMinutes);
  const savedTiming: ScheduleTiming = {
    scheduleType: parseScheduleType(saved?.scheduleType),
    timeOfDay: saved?.timeOfDay || DEFAULT_TIME_OF_DAY,
    daysOfWeek: sortedDays(saved?.daysOfWeek ?? []),
  };
  const timezone = saved?.timezone || browserTimeZone();
  const [enabled, setEnabled] = useState(Boolean(saved?.enabled));
  const [amount, setAmount] = useState(String(initial.amount));
  const [unit, setUnit] = useState<AutoRefreshIntervalUnit>(initial.unit);
  const [timing, setTiming] = useState<ScheduleTiming>(savedTiming);
  const [notifyRecipients, setNotifyRecipients] =
    useState<NotifyRecipient[]>(savedRecipients);
  const [notifyChannels, setNotifyChannels] =
    useState<AutoRefreshNotifyChannel[]>(savedChannels);

  // Re-sync local inputs when the saved schedule changes (after a save or another user's edit).
  const savedKey = `${Boolean(saved?.enabled)}:${savedMinutes}:${timingKey(savedTiming)}:${JSON.stringify(savedRecipients)}:${savedChannels.join(',')}`;
  const [syncedKey, setSyncedKey] = useState(savedKey);
  if (syncedKey !== savedKey) {
    setSyncedKey(savedKey);
    setEnabled(Boolean(saved?.enabled));
    setAmount(String(initial.amount));
    setUnit(initial.unit);
    setTiming(savedTiming);
    setNotifyRecipients(savedRecipients);
    setNotifyChannels(savedChannels);
  }

  const roleScopeMembers = useQuery({
    queryKey: ['role-scope-members', sitrep.group_guid],
    enabled: Boolean(sitrep.group_guid),
    queryFn: async () => {
      const response = await axios.post<RoleScopeMember[]>(
        apiConfig.apiGateway.roleScopeMembersForGroup,
        { group_guid: sitrep.group_guid }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
  const scopeGroups = useMemo(
    () => roleScopeGroups(roleScopeMembers.data ?? []),
    [roleScopeMembers.data]
  );

  const users = directory.data?.users ?? [];
  const roles = directory.data?.roles ?? [];

  const recipientLabel = (recipient: NotifyRecipient) => {
    if (recipient.kind === 'user') {
      return (
        users.find((user) => user.userGuid === recipient.userGuid)
          ?.displayName ?? recipient.userGuid
      );
    }
    if (recipient.scopeId) {
      const group = scopeGroups.find(
        (item) =>
          item.key === roleScopeKey(recipient.roleId, recipient.scopeId ?? '')
      );
      return group
        ? `${group.roleName} · ${group.scopeLabel} (${group.members.length})`
        : 'Role at a scope nobody holds';
    }
    const roleName =
      roles.find((role) => role.roleId === recipient.roleId)?.name ??
      scopeGroups.find((item) => item.roleId === recipient.roleId)?.roleName ??
      recipient.roleId;
    return `Role: ${roleName} (all scopes)`;
  };

  const selectedScopeKeys = new Set(
    notifyRecipients.flatMap((recipient) =>
      recipient.kind === 'role' && recipient.scopeId
        ? [roleScopeKey(recipient.roleId, recipient.scopeId)]
        : []
    )
  );

  const persist = async (
    nextEnabled: boolean,
    intervalMinutes: number,
    recipients: NotifyRecipient[] = notifyRecipients,
    channels: AutoRefreshNotifyChannel[] = notifyChannels,
    nextTiming: ScheduleTiming = timing
  ) => {
    try {
      await mutation.mutateAsync({
        sitrepId: sitrep.id,
        enabled: nextEnabled,
        intervalMinutes,
        scheduleType: nextTiming.scheduleType,
        timeOfDay: nextTiming.timeOfDay,
        daysOfWeek: nextTiming.daysOfWeek,
        timezone,
        notifyRecipients: recipients,
        notifyChannels: channels,
      });
      return true;
    } catch {
      // Roll back the optimistic switch so the UI matches what the server has.
      setEnabled(Boolean(saved?.enabled));
      setTiming(savedTiming);
      setNotifyRecipients(savedRecipients);
      setNotifyChannels(savedChannels);
      toast.error('Could not save auto-refresh.');
      return false;
    }
  };

  const resolveIntervalMinutes = (
    nextAmount: string,
    nextUnit: AutoRefreshIntervalUnit
  ): number | null => validIntervalMinutes(Number(nextAmount), nextUnit);

  /** Save a changed interval; reverts the inputs when the value is not saveable. */
  const persistInterval = (
    nextAmount: string,
    nextUnit: AutoRefreshIntervalUnit
  ) => {
    const intervalMinutes = resolveIntervalMinutes(nextAmount, nextUnit);
    if (intervalMinutes === null) {
      toast.error(
        `Choose a whole number between 1 and ${maxIntervalAmountForUnit(nextUnit)} ${nextUnit}.`
      );
      const revert = minutesToInterval(savedMinutes);
      setAmount(String(revert.amount));
      setUnit(revert.unit);
      return;
    }
    if (
      intervalMinutes === savedMinutes &&
      Boolean(saved?.enabled) &&
      savedTiming.scheduleType === 'interval'
    ) {
      return; // nothing changed
    }
    void persist(true, intervalMinutes);
  };

  const save = async (): Promise<boolean> => {
    const usesInterval = timing.scheduleType === 'interval';
    const intervalMinutes =
      resolveIntervalMinutes(amount, unit) ??
      (enabled && usesInterval ? null : savedMinutes);
    if (intervalMinutes === null) {
      toast.error(
        `Choose a whole number between 1 and ${maxIntervalAmountForUnit(unit)} ${unit}.`
      );
      return false;
    }
    const timingError = enabled ? validateSchedule(timing) : null;
    if (timingError) {
      toast.error(timingError);
      return false;
    }
    const unchanged =
      Boolean(saved?.enabled) === enabled &&
      (!enabled ||
        (timingKey(timing) === timingKey(savedTiming) &&
          (!usesInterval || intervalMinutes === savedMinutes))) &&
      sameRecipients(notifyRecipients, savedRecipients) &&
      sameChannels(notifyChannels, savedChannels);
    if (unchanged) {
      return true;
    }
    return persist(enabled, intervalMinutes, notifyRecipients, notifyChannels);
  };

  /**
   * Apply a schedule type, time, or days change; saves at once only when it's
   * complete.
   */
  const updateTiming = (patch: Partial<ScheduleTiming>) => {
    const next = { ...timing, ...patch };
    setTiming(next);
    if (persistOnChange && enabled && !validateSchedule(next)) {
      const intervalMinutes =
        resolveIntervalMinutes(amount, unit) ?? savedMinutes;
      void persist(
        enabled,
        intervalMinutes,
        notifyRecipients,
        notifyChannels,
        next
      );
    }
  };

  const toggleChannel = (
    channel: AutoRefreshNotifyChannel,
    checked: boolean
  ) => {
    const next = AUTO_REFRESH_CHANNEL_OPTIONS.map(
      (option) => option.value
    ).filter((value) =>
      value === channel ? checked : notifyChannels.includes(value)
    );
    if (next.length === 0) return;
    setNotifyChannels(next);
    if (persistOnChange) {
      const intervalMinutes =
        resolveIntervalMinutes(amount, unit) ?? savedMinutes;
      void persist(enabled, intervalMinutes, notifyRecipients, next);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      save,
      isPending: mutation.isPending,
    }),
    // save closes over latest amount/unit/enabled via this render
    [
      amount,
      unit,
      enabled,
      timing,
      timezone,
      mutation.isPending,
      notifyRecipients,
      notifyChannels,
      saved,
      savedMinutes,
      savedRecipients,
      sitrep.id,
    ]
  );

  const lastDraft = formatLocalTime(saved?.lastRunAt);
  const nextDraft = saved?.enabled ? formatLocalTime(saved?.nextRunAt) : null;
  const lastError = saved?.lastError?.trim() || null;
  const runSummary = autoRefreshRunSummaryText(
    saved?.lastRunSummary,
    formatLocalTime
  );
  const pausedAfterFailures =
    Boolean(lastError) &&
    !saved?.enabled &&
    (saved?.consecutiveFailures ?? 0) > 0;
  const intervalDisabled = !enabled || mutation.isPending;
  const timestampLabel = [
    nextDraft ? `Next draft: ${nextDraft}` : 'Next draft: pending',
    lastDraft ? `Last draft: ${lastDraft}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2"
      data-testid="sitrep-auto-refresh"
    >
      <div className="tw:flex tw:items-start tw:gap-2">
        <Switch
          id={`sitrep-auto-refresh-${sitrep.id}`}
          checked={enabled}
          disabled={mutation.isPending}
          className="tw:mt-0"
          onCheckedChange={(checked) => {
            const nextEnabled = checked === true;
            const intervalMinutes =
              resolveIntervalMinutes(amount, unit) ?? savedMinutes;
            setEnabled(nextEnabled);
            if (persistOnChange) {
              void persist(nextEnabled, intervalMinutes);
            }
          }}
        />
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
          <Label
            htmlFor={`sitrep-auto-refresh-${sitrep.id}`}
            className="tw:leading-5"
          >
            Auto-refresh this report
          </Label>
          <p className="tw:text-sm tw:leading-5 tw:font-normal tw:text-muted-foreground">
            Updates the in-progress draft for this incident or area on the
            interval or at the daily or weekly time you choose, or creates one
            when none exists. Analytics and AI sections rerun. The draft stays
            in Drafts until you submit it.
          </p>
        </div>
      </div>
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Select
          value={timing.scheduleType}
          disabled={intervalDisabled}
          onValueChange={(next) =>
            updateTiming({ scheduleType: parseScheduleType(next) })
          }
        >
          <SelectTrigger
            size="sm"
            className="tw:w-28"
            aria-label="Repeat"
            data-testid="sitrep-auto-refresh-type"
          >
            <SelectValue>
              {SCHEDULE_TYPE_LABELS[timing.scheduleType]}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="dark" positionerClassName="tw:z-[1110]">
            <SelectItem value="interval">Every…</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
          </SelectContent>
        </Select>
        {timing.scheduleType === 'interval' ? (
          <>
            <Label
              htmlFor={`sitrep-auto-refresh-amount-${sitrep.id}`}
              className="tw:sr-only"
            >
              Interval amount
            </Label>
            <Input
              id={`sitrep-auto-refresh-amount-${sitrep.id}`}
              type="number"
              min={1}
              max={maxIntervalAmountForUnit(unit)}
              step={1}
              className="tw:w-20"
              value={amount}
              disabled={intervalDisabled}
              onChange={(event) => setAmount(event.target.value)}
              onBlur={() => {
                if (persistOnChange && enabled) persistInterval(amount, unit);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.currentTarget.blur();
                }
              }}
            />
            <Select
              value={unit}
              disabled={intervalDisabled}
              onValueChange={(next) => {
                const nextUnit = next === 'hours' ? 'hours' : 'minutes';
                setUnit(nextUnit);
                if (persistOnChange && enabled)
                  persistInterval(amount, nextUnit);
              }}
            >
              <SelectTrigger
                size="sm"
                className="tw:w-28"
                aria-label="Interval unit"
              >
                <SelectValue>
                  {unit === 'hours' ? 'hours' : 'minutes'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark" positionerClassName="tw:z-[1110]">
                <SelectItem value="minutes">minutes</SelectItem>
                <SelectItem value="hours">hours</SelectItem>
              </SelectContent>
            </Select>
          </>
        ) : (
          <>
            <Label
              htmlFor={`sitrep-auto-refresh-time-${sitrep.id}`}
              className="tw:text-sm tw:font-normal tw:text-muted-foreground"
            >
              at
            </Label>
            <Input
              id={`sitrep-auto-refresh-time-${sitrep.id}`}
              type="time"
              step={60}
              className="tw:w-32"
              value={timing.timeOfDay}
              disabled={intervalDisabled}
              onChange={(event) =>
                setTiming({ ...timing, timeOfDay: event.target.value })
              }
              onBlur={() => updateTiming({})}
            />
          </>
        )}
      </div>
      {timing.scheduleType === 'weekly' ? (
        <ToggleGroup
          multiple
          size="sm"
          variant="outline"
          spacing={1}
          aria-label="Days of the week"
          disabled={intervalDisabled}
          value={timing.daysOfWeek.map(String)}
          onValueChange={(next) =>
            updateTiming({
              daysOfWeek: sortedDays((next as string[]).map(Number)),
            })
          }
        >
          {WEEKDAYS.map((day) => (
            <ToggleGroupItem
              key={day.value}
              value={String(day.value)}
              aria-label={day.long}
            >
              {day.short}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : null}
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        {timing.scheduleType !== 'interval' ? (
          <span
            className="tw:text-sm tw:text-muted-foreground"
            data-testid="sitrep-auto-refresh-schedule"
          >
            {describeSchedule({ ...timing, timezone })}
          </span>
        ) : null}
        <span
          className={
            intervalDisabled
              ? 'tw:text-sm tw:text-muted-foreground tw:opacity-50'
              : 'tw:text-sm tw:text-muted-foreground'
          }
          aria-disabled={intervalDisabled}
        >
          {timestampLabel}
        </span>
      </div>
      {runSummary ? (
        <p
          className={
            runSummary.warning
              ? 'tw:text-sm tw:text-amber-700'
              : 'tw:text-sm tw:text-muted-foreground'
          }
          data-testid="sitrep-auto-refresh-summary"
        >
          {runSummary.text}
        </p>
      ) : null}
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Label>Notify when this draft is updated</Label>
        <div className="tw:flex tw:flex-wrap tw:gap-1.5">
          {notifyRecipients.map((recipient) => (
            <Badge
              key={recipientKey(recipient)}
              variant="secondary"
              className="tw:h-6 tw:rounded-full tw:pr-1"
            >
              {recipientLabel(recipient)}
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove ${recipientLabel(recipient)}`}
                onClick={() => {
                  const next = notifyRecipients.filter(
                    (item) => recipientKey(item) !== recipientKey(recipient)
                  );
                  setNotifyRecipients(next);
                  if (persistOnChange) {
                    const intervalMinutes =
                      resolveIntervalMinutes(amount, unit) ?? savedMinutes;
                    void persist(enabled, intervalMinutes, next);
                  }
                }}
              >
                <XIcon />
              </Button>
            </Badge>
          ))}
        </div>
        <SearchableSelect
          aria-label="Add a user to notify"
          placeholder="Add a user…"
          options={users.map((user) => ({
            value: user.userGuid,
            label: user.displayName,
          }))}
          value={null}
          onChange={(next) => {
            if (!next) return;
            const recipient: NotifyRecipient = {
              kind: 'user',
              userGuid: next.value,
            };
            if (
              notifyRecipients.some(
                (item) => recipientKey(item) === recipientKey(recipient)
              )
            ) {
              return;
            }
            const updated = [...notifyRecipients, recipient];
            setNotifyRecipients(updated);
            if (persistOnChange) {
              const intervalMinutes =
                resolveIntervalMinutes(amount, unit) ?? savedMinutes;
              void persist(enabled, intervalMinutes, updated);
            }
          }}
          positionerClassName="tw:z-[1110]"
        />
        <RoleScopePicker
          groups={scopeGroups}
          selectedKeys={selectedScopeKeys}
          loading={roleScopeMembers.isLoading}
          onAdd={(group) => {
            const recipient: NotifyRecipient = {
              kind: 'role',
              roleId: group.roleId,
              scopeId: group.scopeId,
            };
            if (
              notifyRecipients.some(
                (item) => recipientKey(item) === recipientKey(recipient)
              )
            ) {
              return;
            }
            const updated = [...notifyRecipients, recipient];
            setNotifyRecipients(updated);
            if (persistOnChange) {
              const intervalMinutes =
                resolveIntervalMinutes(amount, unit) ?? savedMinutes;
              void persist(enabled, intervalMinutes, updated);
            }
          }}
        />
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          An empty list sends nothing. A role notifies the people who hold it at
          that scope when the draft is updated.
        </p>
        <div
          className="tw:flex tw:flex-wrap tw:gap-4"
          role="group"
          aria-label="Notify by"
        >
          {AUTO_REFRESH_CHANNEL_OPTIONS.map((option) => {
            const id = `sitrep-auto-refresh-channel-${option.value}-${sitrep.id}`;
            const checked = notifyChannels.includes(option.value);
            return (
              <div
                key={option.value}
                className="tw:flex tw:items-center tw:gap-2"
              >
                <Checkbox
                  id={id}
                  checked={checked}
                  disabled={
                    mutation.isPending ||
                    (checked && notifyChannels.length === 1)
                  }
                  onCheckedChange={(next) =>
                    toggleChannel(option.value, next === true)
                  }
                />
                <Label htmlFor={id} className="tw:font-normal">
                  {option.label}
                </Label>
              </div>
            );
          })}
        </div>
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Sent only when a refresh changes something. Email and text include a
          link to the updated report.
        </p>
        <ReachabilityWarning
          userIds={notifyRecipients.flatMap((recipient) =>
            recipient.kind === 'user' ? [recipient.userGuid] : []
          )}
          roleScopeKeys={[...selectedScopeKeys]}
          roleIds={notifyRecipients.flatMap((recipient) =>
            recipient.kind === 'role' && !recipient.scopeId
              ? [recipient.roleId]
              : []
          )}
          notificationTypes={notifyChannels}
        />
      </div>
      {lastError ? (
        <p
          className="tw:text-sm tw:text-destructive"
          data-testid="sitrep-auto-refresh-error"
        >
          {pausedAfterFailures
            ? `Auto-refresh paused after ${saved?.consecutiveFailures} failed runs. Last error: ${lastError}`
            : `Last run failed: ${lastError}`}
        </p>
      ) : null}
    </div>
  );
});

SitrepAutoRefreshControls.displayName = 'SitrepAutoRefreshControls';

export default SitrepAutoRefreshControls;
